// pages/NexusApp.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import HeroContainer from '../Components/ui/hero-container';
import ContactTestimonialCarousel from '../Components/contact/ContactTestimonialCarousel';
import FreeTrial from '../Components/about-page/free-trial';
import Carousel from '../Components/ui/carousel';
import { Footer } from '../Components/Footer';
import Button from '../Components/ui/button/Button';
import HeaderAlt from '../Components/HeaderAlt';

const NexusApp = () => {
  const navigate = useNavigate();

  return (
    <>
      <HeaderAlt />
      <HeroContainer className="flex pb-[118px] pt-14">
        <div className="container flex flex-col gap-6 !px-6 md:flex-row md:justify-between md:gap-20">
          <div className="max-w-[570px] space-y-4 text-left md:space-y-6">
            <h1 className="fredokaOne text-[40px] font-normal text-white md:text-[56px]">Nexus AI Session Recorder</h1>
            <p className="fredoka text-[18px] text-[#678285] md:text-[22px]">
              Automatically record tutoring sessions, generate transcripts, and insightful summaries to enhance your teaching experience.
            </p>
            <Button
              label="Download Nexus App"
              onClick={() => window.open('/downloads/NexusAppInstaller.dmg', '_blank')}
            />
          </div>
          <div className="mt-6 flex-1 md:mt-0">
            <img src="/images/nexus/hero-1.png" alt="Nexus App Screenshot" />
          </div>
        </div>
      </HeroContainer>

      {/* Features Section */}
      <div className="container !mt-14 grid gap-6 md:grid-cols-3">
        {[
          {
            title: 'Automatic Session Recording',
            desc: 'Effortlessly record every tutoring session with a single click.',
          },
          {
            title: 'Detailed Transcriptions',
            desc: 'Generate accurate, timestamped transcripts for easy reference and student review.',
          },
          {
            title: 'AI-Generated Summaries',
            desc: 'Receive concise, actionable summaries after every session, highlighting key points and next steps.',
          },
        ].map((item, i) => (
          <div key={i} className="rounded-[24px] bg-white p-6 shadow-md">
            <h3 className="fredoka text-[24px] font-semibold text-[#012E33]">{item.title}</h3>
            <p className="text-[18px] text-[#678285] mt-2">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* How It Works */}
      <div className="container mt-20 space-y-10 text-center">
        <h2 className="fredokaOne text-[32px] md:text-[40px] text-[#012E33]">How Nexus Works</h2>
        <div className="grid gap-8 md:grid-cols-3 text-left">
          {[
            {
              step: 'Step 1: Download and Install',
              desc: 'Download the Nexus app and install it easily on your desktop.',
            },
            {
              step: 'Step 2: One-click Session Recording',
              desc: 'Start your tutoring session, and Nexus automatically captures audio and video.',
            },
            {
              step: 'Step 3: Receive Instant Transcripts and Summaries',
              desc: 'After the session, Nexus generates an accurate transcript and a concise, AI-driven summary for review and student sharing.',
            },
          ].map((item, i) => (
            <div key={i} className="rounded-[24px] p-6 bg-[#F7F7F7]">
              <h4 className="fredoka text-[22px] font-semibold">{item.step}</h4>
              <p className="text-[#678285] text-[18px] mt-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Features */}
      <div className="container !mt-20 flex flex-col gap-14">
        {[
          {
            title: 'Easy Installation & Setup',
            text: 'Nexus is user-friendly and quick to install. Get started in minutes with no technical expertise required.',
            img: '/images/nexus/setup.png',
          },
          {
            title: 'AI-Driven Transcript Generation',
            text: 'Automatically generate precise transcripts to reference key teaching moments or provide students with valuable study resources.',
            img: '/images/nexus/transcript.png',
          },
          {
            title: 'Session Summaries & Insights',
            text: 'Gain clear, actionable insights from each tutoring session, helping you refine your teaching strategies and focus areas.',
            img: '/images/nexus/summary.png',
          },
          {
            title: 'Secure and Private',
            text: 'Nexus prioritizes your data security, ensuring your session recordings and summaries remain confidential and accessible only by you.',
            img: '/images/nexus/security.png',
          },
        ].map((block, i) => (
          <div
            key={i}
            className={`flex flex-col md:flex-row ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''} items-center gap-8`}
          >
            <img src={block.img} alt="" className="w-full md:w-1/2 rounded-[24px]" />
            <div className="flex-1 space-y-4">
              <h3 className="fredoka text-[28px] text-[#012E33]">{block.title}</h3>
              <p className="text-[18px] text-[#678285]">{block.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Benefits Section */}
      <div className="container mt-20 space-y-10">
        <h2 className="text-center text-[40px] text-[#012E33]">Benefits of using Nexus</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {[
            ['Save Time', 'Automate session note-taking and summary generation.'],
            ['Enhance Teaching Quality', 'Use detailed summaries to refine teaching strategies.'],
            ['Improved Student Outcomes', 'Provide students with clear, valuable session resources.'],
            ['Organized Documentation', 'Keep structured, easily accessible session records.'],
          ].map(([title, desc], i) => (
            <div key={i} className="rounded-[24px] bg-white p-6 shadow-sm">
              <h3 className="fredoka text-[24px] font-semibold">{title}</h3>
              <p className="text-[18px] text-[#678285] mt-1">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="container my-20 text-center space-y-6">
        <h2 className="fredokaOne text-[40px] text-[#012E33]">Ready to Transform Your Tutoring Sessions?</h2>
        <p className="text-[18px] text-[#678285]">
          Download Nexus today and experience streamlined, AI-powered tutoring sessions.
        </p>
        <Button
          label="Download Now"
          onClick={() => window.open('/downloads/NexusAppInstaller.dmg', '_blank')}
        />
      </div>

      <FreeTrial />
      <Footer />
    </>
  );
};

export default NexusApp;
